import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import React, { useEffect, lazy, Suspense, useState } from 'react';
import ErrorBoundary from '@/components/ErrorBoundary';
import CookieNotice from '@/components/ui/cookie-notice';
import AnnouncementBanner from '@/components/ui/announcement-banner';
import { getAnnouncementConfig, isAnnouncementEnabled, AnnouncementConfig } from '@/lib/announcement-config';

// Import non-lazy components
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import DPA from './pages/DPA';

// Lazy-loaded components
const PrivacyPage = lazy(() => import("./pages/Privacy"));
const TermsPage = lazy(() => import("./pages/Terms"));
const FaqPage = lazy(() => import("./pages/Faq"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Contact = lazy(() => import("./pages/Contact"));
const About = lazy(() => import("./pages/About"));
const BusinessCase = lazy(() => import("./pages/resources/BusinessCase"));
const Glossary = lazy(() => import("./pages/Glossary"));
const GlossaryEntry = lazy(() => import("./pages/GlossaryEntry"));
const KnowledgeIntelligence = lazy(() => import("./pages/KnowledgeIntelligence"));
const CookiesPage = lazy(() => import("./pages/Cookies"));

// Enhance the PageLoader component with a better UI
const PageLoader = () => (
  <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
    <div className="w-16 h-16 border-t-4 border-b-4 border-blue-500 rounded-full animate-spin mb-4"></div>
    <p className="text-gray-600 text-lg">Loading content...</p>
  </div>
);

const queryClient = new QueryClient();

const App: React.FC = () => {
  // State for the announcement banner from config
  const [announcementConfig, setAnnouncementConfig] = useState<AnnouncementConfig | null>(null);
  const [isAnnouncementLoaded, setIsAnnouncementLoaded] = useState(false);

  // Load announcement config
  useEffect(() => {
    const loadAnnouncementConfig = async () => {
      try {
        const config = await getAnnouncementConfig();
        setAnnouncementConfig(config);
        setIsAnnouncementLoaded(true);
      } catch (error) {
        console.error('Failed to load announcement configuration:', error);
        setIsAnnouncementLoaded(true); // Still mark as loaded even if error
      }
    };

    loadAnnouncementConfig();
  }, []);

  // Handle direct access to manifest file
  useEffect(() => {
    // Create a route to handle direct access to site.webmanifest
    if (window.location.pathname === '/site.webmanifest') {
      window.location.href = '/site.webmanifest';
    }

    // Global error handler for Sentry-related errors
    const handleError = (event: ErrorEvent) => {
      // Check if the error is related to Sentry/Cloudflare
      if (event.filename?.includes('sentry') || event.filename?.includes('cloudflare')) {
        // Prevent the error from being logged to console
        event.preventDefault();
        // Optionally log to a different error tracking service
        console.warn('Error tracking service unavailable');
      }
    };

    window.addEventListener('error', handleError);

    return () => {
      window.removeEventListener('error', handleError);
    };
  }, []);
  
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <Router>
              {isAnnouncementLoaded && announcementConfig && isAnnouncementEnabled(announcementConfig) && (
                <AnnouncementBanner 
                  message={announcementConfig.announcement.message}
                  announcementId={announcementConfig.announcement.id}
                  backgroundColor={announcementConfig.announcement.backgroundColor}
                  textColor={announcementConfig.announcement.textColor}
                  showIcon={announcementConfig.announcement.showIcon}
                  ctaText={announcementConfig.announcement.ctaText}
                  ctaLink={announcementConfig.announcement.ctaLink}
                  isInternalLink={announcementConfig.announcement.isInternalLink}
                />
              )}
              <div className="min-h-screen flex flex-col">
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    <Route path="/" element={<Index />} />
                    <Route path="/privacy" element={<PrivacyPage />} />
                    <Route path="/terms" element={<TermsPage />} />
                    <Route path="/cookies" element={<CookiesPage />} />
                    <Route path="/faq" element={<FaqPage />} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/resources/business-case" element={<BusinessCase />} />
                    <Route path="/glossary" element={<Glossary />} />
                    <Route path="/glossary/:slug" element={<GlossaryEntry />} />
                    <Route path="/knowledge-intelligence" element={<KnowledgeIntelligence />} />
                    <Route path="/dpa" element={<DPA />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
                <CookieNotice />
              </div>
            </Router>
          </TooltipProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
};

export default App;
